import _Drawer from "@mui/material/Drawer";
import _FormControl from "@mui/material/FormControl";
import _FormControlLabel from "@mui/material/FormControlLabel";
import _InfoOutlined from "@mui/icons-material/InfoOutlined";
import _Switch from "@mui/material/Switch";
import _ExploreOutlined from "@mui/icons-material/ExploreOutlined";
import _TerrainOutlined from "@mui/icons-material/TerrainOutlined";
import _ButtonBase from "@mui/material/ButtonBase";
import _Divider from "@mui/material/Divider";
import _Toolbar from "@mui/material/Toolbar";
import _Box from "@mui/material/Box";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Typography from "@mui/material/Typography";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { lighten } from '@mui/material/styles';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import aerialTileThumbnail from '../../assets/images/layers/aerial.jpeg';
import darkTileThumbnail from '../../assets/images/layers/dark.png';
import geoveloTileThumbnail from '../../assets/images/layers/geovelo.png';
import ignTileThumbnail from '../../assets/images/layers/ign.jpeg';
import geoveloTonerThumbnail from '../../assets/images/layers/toner.png';
import bicycleHomeIcon from '../../assets/images/poi/accueil-velo.png';
import { baseLayersMap } from '../../hooks';
import { poiCategoryCodes } from '../../models';
import { BikeIcon, ParkingIcon, RoadworksIcon, VerticalGainIcon } from '../icons';
import { FacilitiesLegend } from './facilities-legend';
export const layersDrawerWidth = 320;
export const baseLayersThumbnails = {
  geovelo: geoveloTileThumbnail,
  aerial: aerialTileThumbnail,
  ign: ignTileThumbnail,
  dark: darkTileThumbnail,
  toner: geoveloTonerThumbnail
};
export const defaultBaseLayers = ['geovelo', 'aerial', 'ign'];
const categoriesDisabled = ['bicycleHome', 'tourismOffice'];
const lavCategoriesNotBicycleHome = ['lavLocalProducts', 'lavPackedLunch', 'lavBicycleStopingArea'];
export function LayersDrawer({
  smallDevice,
  baseLayer,
  disableLayerSelection,
  disableParkings,
  facilitiesShowed,
  widgetConfig,
  layers,
  layersShowed,
  enableFacilitiesToggle,
  enableHillshadesToggle,
  hillshadesShowed,
  enableTerrainToggle,
  terrainShowed,
  enableHeatmapToggle,
  heatmapShowed,
  loadingHeatmap,
  poiOptions,
  enableReportsToggle,
  reportsShowed,
  poiCategories,
  selectedPoiCategories,
  onBaseLayerChange,
  toggleFacilities,
  toggleLayers,
  toggleHillshades,
  toggleTerrain,
  toggleHeatmap,
  setPoiOptions,
  toggleReports,
  selectPoiCategories,
  onClose,
  ...props
}) {
  const {
    t
  } = useTranslation();
  function handlePoiCategoryToggle({
    code,
    parentCode,
    children
  }, _selected) {
    const selected = _selected !== undefined ? _selected : !selectedPoiCategories[code];
    const _selectedPoiCategories = {
      ...selectedPoiCategories,
      [code]: selected
    };
    children.forEach(({
      code
    }) => _selectedPoiCategories[code] = selected);
    if (parentCode) {
      const parent = poiCategories?.find(({
        code
      }) => code === parentCode);
      if (selected) {
        if (parent && parent.children.every(({
          code
        }) => _selectedPoiCategories[code] === true)) _selectedPoiCategories[parentCode] = true;
      } else _selectedPoiCategories[parentCode] = false;
    }
    try {
      localStorage.setItem('poi_categories', poiCategoryCodes.filter(code => _selectedPoiCategories[code]).join(','));
    } catch {
      console.error('localStorage access is denied');
    }
    selectPoiCategories(_selectedPoiCategories);
  }
  return /*#__PURE__*/React.createElement(StyledDrawer, _extends({
    anchor: "left",
    className: smallDevice ? 'full-width' : '',
    variant: "persistent"
  }, props), /*#__PURE__*/React.createElement(_Toolbar, {
    sx: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      paddingX: 5,
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 600,
    variant: "subtitle1"
  }, t('commons.layers_drawer.title')), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    height: "100%",
    position: "absolute",
    right: 8,
    top: 0
  }, /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.close"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    onClick: onClose,
    size: "small"
  }, /*#__PURE__*/React.createElement(_Close, null))))), /*#__PURE__*/React.createElement(_Divider, null), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 2,
    paddingY: 2,
    sx: {
      overflowY: 'auto'
    }
  }, !disableLayerSelection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    paddingX: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "caption"
  }, t('commons.layers_drawer.baselayer').toUpperCase()), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    gap: 2
  }, defaultBaseLayers.map(key => {
    const active = key === baseLayer;
    return /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      key: key,
      width: `calc((100% - ${(defaultBaseLayers.length - 1) * 16}px) / ${defaultBaseLayers.length})`
    }, /*#__PURE__*/React.createElement(_Box, {
      alignSelf: "stretch",
      borderRadius: 2,
      component: active ? 'div' : _ButtonBase,
      height: active ? 64 : 68,
      onClick: () => !active && onBaseLayerChange?.(key),
      sx: ({
        palette
      }) => ({
        backgroundImage: `url(${baseLayersThumbnails[key]})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        border: `2px solid ${active ? palette.primary.main : 'transparent'}`
      })
    }), /*#__PURE__*/React.createElement(_Typography, {
      variant: "caption"
    }, t(baseLayersMap[key].labelKey || '')));
  }))), /*#__PURE__*/React.createElement(_Divider, null)), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    paddingX: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "caption"
  }, t('commons.layers_drawer.details').toUpperCase())), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    sx: {
      'fieldset:not(:last-child)': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }
    }
  }, enableFacilitiesToggle && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "#03966a",
    Icon: BikeIcon,
    info: /*#__PURE__*/React.createElement(FacilitiesLegend, {
      enableDescriptions: true,
      layers: layers
    }),
    label: t('commons.facilities_label'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_facilities', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      toggleFacilities?.(enabled);
    },
    value: facilitiesShowed || false
  }), widgetConfig?.code === 'larochelle' && layers?.[0] && /*#__PURE__*/React.createElement(SwitchControl, {
    label: layers[0].name,
    onChange: enabled => toggleLayers?.(enabled),
    value: layersShowed || false
  }), enableReportsToggle && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "#ee7734",
    Icon: RoadworksIcon,
    label: t('commons.reports_label'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_reports', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      toggleReports?.(enabled);
    },
    value: reportsShowed || false
  }), enableHillshadesToggle && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "#212121",
    Icon: VerticalGainIcon,
    label: t('commons.hillshades_label'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_hillshades', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      toggleHillshades?.(enabled);
    },
    value: hillshadesShowed || false
  }), enableTerrainToggle && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "#212121",
    Icon: _TerrainOutlined,
    info: t('commons.terrain_info'),
    label: t('commons.terrain_label'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_terrain', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      toggleTerrain?.(enabled);
    },
    value: terrainShowed || false
  }), enableHeatmapToggle && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "rgb(73, 116, 165)",
    disabled: loadingHeatmap,
    Icon: _ExploreOutlined,
    label: t('commons.heatmap_label'),
    onChange: enabled => {
      toggleHeatmap?.(enabled);
    },
    value: heatmapShowed || false
  }), !disableParkings && !poiCategories?.find(({
    code
  }) => code === 'bicycleParking') && /*#__PURE__*/React.createElement(SwitchControl, {
    color: "#326ac2",
    Icon: ParkingIcon,
    label: t('commons.poi.categories.parking_other'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_parking_spaces', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      setPoiOptions?.({
        ...poiOptions,
        displayParkingSpaces: enabled
      });
    },
    value: poiOptions?.displayParkingSpaces || false
  }))), widgetConfig?.code === 'lav' && /*#__PURE__*/React.createElement(SwitchControl, {
    icon: bicycleHomeIcon,
    info: t('commons.poi.bicycle_home_restriction'),
    label: t('commons.poi.categories.lav_bicycle_home'),
    onChange: enabled => {
      try {
        localStorage.setItem('display_only_bicycle_home', enabled ? 'true' : 'false');
      } catch {
        console.error('localStorage access is denied');
      }
      setPoiOptions?.({
        ...poiOptions,
        displayOnlyBicycleHome: enabled
      });
    },
    value: poiOptions?.displayOnlyBicycleHome || false
  }), poiCategories?.filter(({
    parentCode
  }) => parentCode === null).sort((a, b) => a.order - b.order).map(category => {
    const {
      id,
      code,
      labelKey,
      color,
      icon
    } = category;
    const children = poiCategories.filter(({
      code: childCode,
      parentCode
    }) => parentCode === code && !categoriesDisabled.includes(childCode) && (widgetConfig?.code !== 'lav' || !poiOptions?.displayOnlyBicycleHome || !lavCategoriesNotBicycleHome.includes(childCode)));
    return /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      key: id
    }, /*#__PURE__*/React.createElement(_Box, {
      paddingX: 2
    }, /*#__PURE__*/React.createElement(_Typography, {
      color: "textSecondary",
      variant: "caption"
    }, t(`${labelKey}_other`).toUpperCase())), /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      flexDirection: "column",
      sx: {
        'fieldset:not(:last-child)': {
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }
      }
    }, children.length === 0 ? /*#__PURE__*/React.createElement(SwitchControl, {
      color: color || '',
      icon: icon,
      label: t(`${labelKey}_other`),
      onChange: () => handlePoiCategoryToggle(category),
      value: selectedPoiCategories[code] || false
    }) : children.sort((a, b) => a.order - b.order).map(childCategory => {
      const {
        id: childId,
        code: childCode,
        labelKey: childLabelKey,
        color: childColor,
        icon: childIcon
      } = childCategory;
      if (childCode === 'bicycleParking') {
        return !disableParkings && /*#__PURE__*/React.createElement(SwitchControl, {
          color: "#326ac2",
          Icon: ParkingIcon,
          key: childId,
          label: t('commons.poi.categories.parking_other'),
          onChange: enabled => {
            try {
              localStorage.setItem('display_parking_spaces', enabled ? 'true' : 'false');
            } catch {
              console.error('localStorage access is denied');
            }
            setPoiOptions?.({
              ...poiOptions,
              displayParkingSpaces: enabled
            });
          },
          value: poiOptions?.displayParkingSpaces || false
        });
      }
      return /*#__PURE__*/React.createElement(SwitchControl, {
        color: childColor || color || '',
        icon: childIcon,
        key: childId,
        label: t(`${childLabelKey}_other`),
        onChange: () => handlePoiCategoryToggle(childCategory),
        value: selectedPoiCategories[childCode] || false
      });
    })));
  })));
}
function SwitchControl({
  disabled,
  value,
  Icon,
  icon,
  label,
  color,
  info,
  onChange
}) {
  return /*#__PURE__*/React.createElement(_FormControl, {
    component: "fieldset",
    disabled: disabled,
    margin: "none",
    sx: {
      paddingRight: 2
    }
  }, /*#__PURE__*/React.createElement(_FormControlLabel, {
    disableTypography: true,
    control: /*#__PURE__*/React.createElement(_Switch, {
      checked: value,
      color: "primary",
      onChange: (_, enabled) => onChange(enabled),
      value: value
    }),
    label: /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexGrow: 1,
      gap: 2,
      height: 48
    }, icon || Icon ? /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      borderRadius: 16,
      display: "flex",
      height: 32,
      justifyContent: "center",
      sx: {
        backgroundColor: Icon && color ? lighten(color, 0.8) : color
      },
      width: 32
    }, Icon ? /*#__PURE__*/React.createElement(Icon, {
      sx: {
        color
      }
    }) : icon && /*#__PURE__*/React.createElement("img", {
      src: icon,
      width: 20
    })) : /*#__PURE__*/React.createElement(_Box, {
      width: 32
    }), /*#__PURE__*/React.createElement(_Typography, null, label), info && /*#__PURE__*/React.createElement(_Tooltip, {
      placement: "right",
      slotProps: {
        popper: {
          sx: ({
            shadows
          }) => ({
            '> div': {
              backgroundColor: '#fff',
              boxShadow: shadows[1],
              color: 'rgba(0, 0, 0, 0.87)',
              padding: 2
            }
          })
        }
      },
      title: info
    }, /*#__PURE__*/React.createElement(_InfoOutlined, {
      color: "action",
      fontSize: "small"
    }))),
    labelPlacement: "start"
  }));
}
const StyledDrawer = styled(_Drawer)`
  width: ${`${layersDrawerWidth}px`};

  && > .MuiPaper-root {
    border: 1px solid rgba(0, 0, 0, 0.12);
    width: ${`${layersDrawerWidth}px`};
  }

  &.full-width {
    width: 100%;

    && > .MuiPaper-root {
      width: 100%;
    }
  }
`;